import { TFunction } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouterProvider, Route, Navigate, createBrowserRouter, createRoutesFromElements } from 'react-router-dom'

// Components
import Layout from '../Layout'
import ProtectedMasterRoute from '../ProtectedMasterRoute'
import ProtectedMcatRoute from '../ProtectedMcatRoute'
import ProtectedRoute from '../ProtectedRoute'
import PaymentsSettingsError from './PaymentsSettingsError'
import SuspenseRoute from './SuspenseRoute'
import {
  getProtectedMasterRoutes,
  getProtectedMcatRoutes,
  getProtectedRoutes,
  publicRoutes,
  RouteType,
} from './routeConfig'
import useGetCurrentTaxYearUsePayments, { isPaymentsInUse } from '@/hooks/useGetCurrentTaxYearUsePayments'
import useGlobalContext from '@/hooks/useGlobalContext'

// Pages
const PageNotFound = React.lazy(() => import('../../pages/PageNotFound'))

interface GuardProps {
  permission?: string | string[]
  requiresPayments?: boolean
  children: React.ReactNode
}

function Guard({ permission, requiresPayments, children }: GuardProps): JSX.Element {
  const { hasPermission } = useGlobalContext()
  const {
    data: usePaymentsData,
    isLoading: isLoadingUsePayments,
    isError: isUsePaymentsError,
    isFetching: isFetchingUsePayments,
    refetch: refetchUsePayments,
  } = useGetCurrentTaxYearUsePayments()

  if (!hasPermission(permission)) {
    return <Navigate to="/unauthorized" />
  }

  if (requiresPayments) {
    if (isLoadingUsePayments) {
      return <></>
    }

    if (isUsePaymentsError) {
      return <PaymentsSettingsError isRetrying={isFetchingUsePayments} tryAgain={() => refetchUsePayments()} />
    }

    if (!isPaymentsInUse(usePaymentsData)) {
      return <Navigate to="/unauthorized" />
    }
  }

  return <>{children}</>
}

function renderRoutes(routes: RouteType[]): JSX.Element[] {
  return routes.map(({ path, component: Component, children, permission, requiresPayments }, index) => (
    <Route
      key={`${path}-${index}`}
      path={path}
      element={
        <Guard permission={permission} requiresPayments={requiresPayments}>
          <SuspenseRoute Component={Component as React.ComponentType} />
        </Guard>
      }
    >
      {children && renderRoutes(children)}
    </Route>
  ))
}

export function AppRouter(): JSX.Element {
  const { t } = useTranslation()

  const router = React.useMemo(() => {
    return createBrowserRouter(createRouteElements(t))
  }, [t])

  return <RouterProvider router={router} />
}

export function createRouteElements(t: TFunction): ReturnType<typeof createRoutesFromElements> {
  return createRoutesFromElements(
    <>
      <Route element={<Layout />}>
        {/* public */}
        {renderRoutes(publicRoutes)}

        {/* protected */}
        <Route element={<ProtectedRoute />}>{renderRoutes(getProtectedRoutes(t))}</Route>

        {/* protected master */}
        <Route element={<ProtectedMasterRoute />}>{renderRoutes(getProtectedMasterRoutes(t))}</Route>

        {/* protected mcat */}
        <Route element={<ProtectedMcatRoute />}>{renderRoutes(getProtectedMcatRoutes(t))}</Route>
      </Route>

      <Route element={<Layout isPageNotFound />}>
        <Route path="*" element={<SuspenseRoute Component={PageNotFound} />} />
      </Route>
    </>,
  )
}
